import React, { useCallback, useMemo, useRef } from "react";
import { useTab, useTabList } from "react-aria";

import { cn } from "../../utils/cn";
import { Button } from "../button/Button";
import { Tooltip } from "../tooltip/Tooltip";

import type { Node } from "@react-types/shared";
import type { ReactElement } from "react";
import type { TabListState } from "react-stately";
import type { SidebarTabsState } from "../../hooks/useSidebarTabsState";

// ─────────────────────────────────────────────────────────────────────────────
// ActivityBar Item - the unit of rendering for the ActivityBar
// ─────────────────────────────────────────────────────────────────────────────

/**
 * An item rendered in the ActivityBar.
 * This is intentionally minimal - just what's needed to render an icon button.
 */
export interface ActivityBarItem {
  /** Unique identifier for the item. */
  id: string;
  /** Icon component to render. */
  icon: React.ElementType;
  /** Label shown in tooltip and for accessibility. */
  label: string;
  /** Optional badge content. */
  badge?: string | number | React.ReactNode;
  /** Optional badge color class. */
  badgeColor?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Simple ActivityBar (props-based)
// ─────────────────────────────────────────────────────────────────────────────

export interface ActivityBarProps {
  /** Items to render in the activity bar. */
  items: ActivityBarItem[];
  /** Currently active item id, or null if none selected. */
  activeItemId: string | null;
  /** Called when an item is selected via click. */
  onItemSelect: (id: string) => void;
  /** Items anchored at the bottom of the bar (e.g., settings). */
  bottomItems?: ActivityBarItem[];
  className?: string;
}

/**
 * Vertical Activity Bar.
 *
 * This component is intentionally agnostic: it only renders icons and
 * reports which item was selected. Higher-level layout components decide
 * how selection affects sidebars, tabs, navigation, etc.
 */
export function ActivityBar({
  items,
  activeItemId,
  onItemSelect,
  bottomItems,
  className,
}: ActivityBarProps): ReactElement {
  const hasItems = items.length > 0;
  const hasBottomItems = bottomItems && bottomItems.length > 0;

  return (
    <div
      className={cn(
        "flex h-full w-14 min-w-[56px] flex-col items-center gap-2 bg-layer-layer1 py-2",
        "border-y-0 border-l-0 border-r border-solid border-gray-200 dark:border-gray-700",
        className
      )}
    >
      <div
        className="flex flex-1 flex-col items-center gap-2"
        role="tablist"
        aria-orientation="vertical"
      >
        {hasItems &&
          items.map(item => (
            <ActivityBarButton
              key={item.id}
              item={item}
              isActive={activeItemId === item.id}
              onSelect={onItemSelect}
            />
          ))}
      </div>
      {hasBottomItems && (
        <div className="flex flex-col items-center gap-2 border-t border-gray-200 pt-2 dark:border-gray-700">
          {bottomItems.map(item => (
            <ActivityBarButton
              key={item.id}
              item={item}
              isActive={activeItemId === item.id}
              onSelect={onItemSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface ActivityBarButtonProps {
  item: ActivityBarItem;
  isActive: boolean;
  onSelect: (id: string) => void;
}

function ActivityBarButton({ item, isActive, onSelect }: ActivityBarButtonProps): ReactElement {
  const { id, icon: Icon, label, badge, badgeColor } = item;

  const handlePress = useMemo(
    () => () => {
      onSelect(id);
    },
    [id, onSelect]
  );

  return (
    <div className="relative flex justify-center">
      <Tooltip content={label} placement="right" color="default">
        <Button
          onPress={handlePress}
          aria-label={label}
          role="tab"
          aria-selected={isActive}
          variant="light"
          ghost
          size="lg"
          isIconOnly
          className={cn(
            "relative flex size-10 items-center justify-center rounded-sm transition-colors",
            isActive
              ? "bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-50"
              : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
          )}
        >
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {React.createElement(Icon as any, { className: "size-6", "aria-hidden": "true" })}
        </Button>
      </Tooltip>
      {badge && (
        <div
          className={cn(
            "pointer-events-none absolute bottom-1 right-1 flex min-w-[16px] select-none items-center justify-center rounded-full px-1 text-[10px] font-bold text-white shadow-sm ring-1 ring-white dark:ring-gray-900",
            badgeColor ?? "bg-blue-600"
          )}
        >
          {badge}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// State-based ActivityBar with react-aria accessibility
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Alias for ActivityBarItem when used with react-stately collections.
 * @deprecated Use ActivityBarItem directly
 */
export type ActivityBarItemValue = ActivityBarItem;

export interface StateActivityBarProps<T extends ActivityBarItem> {
  /** The tab list state from useSidebarTabsState. */
  state: SidebarTabsState<T> | TabListState<T>;
  className?: string;
  /** ARIA label for the tab list. */
  "aria-label"?: string;
}

/**
 * State-based Activity Bar that uses react-aria for full accessibility.
 *
 * Use this variant when you're managing state with useSidebarTabsState
 * or useTabListState from react-stately.
 *
 * @example
 * ```tsx
 * const state = useSidebarTabsState({
 *   children: views.map(v => <Item key={v.id}>{v}</Item>),
 * });
 *
 * <StateActivityBar state={state} />
 * ```
 */
export function StateActivityBar<T extends ActivityBarItem>({
  state,
  className,
  "aria-label": ariaLabel = "Activity Bar",
}: StateActivityBarProps<T>): ReactElement {
  const ref = useRef<HTMLDivElement>(null);

  const { tabListProps } = useTabList(
    {
      "aria-label": ariaLabel,
      orientation: "vertical",
    },
    state,
    ref
  );

  return (
    <div
      className={cn(
        "flex h-full w-14 min-w-[56px] flex-col items-center gap-2 bg-layer-layer0 py-3",
        "border-y-0 border-l-0 border-r border-solid border-gray-200 dark:border-gray-700",
        className
      )}
    >
      <div {...tabListProps} ref={ref} className="flex flex-1 flex-col items-center gap-2">
        {[...state.collection].map(item => (
          <StateActivityBarItem key={item.key} item={item} state={state} />
        ))}
      </div>
    </div>
  );
}

interface StateActivityBarItemProps<T extends ActivityBarItemValue> {
  item: Node<T>;
  state: SidebarTabsState<T> | TabListState<T>;
}

function StateActivityBarItem<T extends ActivityBarItemValue>({
  item,
  state,
}: StateActivityBarItemProps<T>): ReactElement {
  const ref = useRef<HTMLButtonElement>(null);
  const { tabProps } = useTab({ key: item.key }, state, ref);

  const value = item.value;
  const isSelected = state.selectedKey === item.key;

  // Handle sidebar-specific selection if using SidebarTabsState
  const handlePress = useCallback(() => {
    if ("selectTab" in state) {
      state.selectTab(item.key);
    } else {
      state.setSelectedKey(item.key);
    }
  }, [state, item.key]);

  if (!value) {
    return <></>;
  }

  const { icon: Icon, label, badge, badgeColor } = value;

  return (
    <div className="relative flex justify-center">
      <Tooltip content={label} placement="right" color="default">
        <button
          {...tabProps}
          ref={ref}
          onClick={handlePress}
          className={cn(
            "relative flex size-10 items-center justify-center rounded-sm outline-none transition-colors",
            "focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
            isSelected
              ? "bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-50"
              : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
          )}
        >
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {React.createElement(Icon as any, { className: "size-6", "aria-hidden": "true" })}
        </button>
      </Tooltip>
      {badge && (
        <div
          className={cn(
            "pointer-events-none absolute bottom-1 right-1 flex min-w-[16px] select-none items-center justify-center rounded-full px-1 text-[10px] font-bold text-white shadow-sm ring-1 ring-white dark:ring-gray-900",
            badgeColor ?? "bg-blue-600"
          )}
        >
          {badge}
        </div>
      )}
    </div>
  );
}
